import { type FormEvent, type JSX, useCallback, useEffect, useState } from 'react';

import {
  bausteinAendern,
  bausteinAnlegen,
  bausteine as ladeBausteine,
  BAUSTEINARTEN,
  BAUSTEINARTNAME,
  type Bausteinart,
  type Bausteinzeile,
} from '../api/bausteine';
import {
  bedarf as ladeBedarf,
  bedarfSetzen,
  RESSOURCENARTEN,
  RESSOURCENARTNAME,
  type Bedarfseigenschaft,
  type Bedarfseingabe,
} from '../api/bausteine';
import { ApiFehler } from '../api/fehler';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';
import { Liste } from '../komponenten/Liste';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Der Bausteinkatalog (PRD-29, PRD-30).
 *
 * ============================================================================
 * DREI FELDER, DREI VERSCHIEDENE FREIHEITEN
 * ============================================================================
 *
 * Der SCHLUESSEL ist die Identitaet. Sechs Tabellen verweisen darauf, keine
 * mit ON UPDATE CASCADE — er wird bei der Anlage vergeben und danach nie
 * wieder angefasst. Die Maske zeigt ihn deshalb bei der Aenderung als Text
 * und nicht als Feld: Ein graues Eingabefeld laedt trotzdem zum Tippen ein.
 *
 * Die ART rechnet. Eine Pause zaehlt nicht in die Mindestdauer ohne Pausen
 * (PRD-23), und die Pausenregeln nach TAG-18 greifen nur an ihr. Sobald der
 * Baustein in einem Plan steht, wuerde eine Aenderung einen laengst
 * gefahrenen Tag rueckwirkend anders rechnen — PRD-30 verbietet das, und die
 * Datenbank weist es ab. Die Maske sperrt das Feld dann und SAGT WARUM,
 * statt den Anwender in eine Abweisung laufen zu lassen.
 *
 * Alles UEBRIGE ist frei — auch am verplanten Baustein. Die Regeldauer ist
 * eine Eingangsgroesse der Planung: Aus ihr entstehen beim Planen die
 * Bloecke, die dann mit Beginn und Ende dastehen. Ein neuer Wert verschiebt
 * keinen bestehenden Block, er wirkt beim naechsten Planen. Genau das meint
 * PRD-30 mit "wirkt nur auf kuenftig angelegte Termine".
 *
 * ============================================================================
 * ES WIRD STILLGELEGT UND NICHT GELOESCHT
 * ============================================================================
 *
 * Eignungen und Ressourcenbedarfe haengen mit ON DELETE CASCADE am Baustein.
 * Ein Loeschen naehme also die Eignungen von elf Modulen still mit. Der Weg
 * ist `aktiv = false` (DAT-25): Der Baustein verschwindet aus jeder Auswahl,
 * alles Bestehende bleibt lesbar. Die Maske bietet deshalb kein Loeschen an.
 */

/**
 * PRD-39: Arten, die niemand kauft.
 *
 * Begrüßung, Pause und Abschluss finden statt, sind aber Organisation und
 * kein Inhalt. Die Datenbank weist sie im Leistungsversprechen ab (Migration
 * 0130), und der Server leitet den Wert ohnehin aus der Art ab — die Maske
 * zeigt für sie deshalb gar kein Kästchen, statt eines anzubieten, das nichts
 * bewirkt.
 */
const OHNE_LEISTUNGSVERSPRECHEN: readonly Bausteinart[] = ['begruessung', 'pause', 'abschluss'];

const LEER = {
  schluessel: '',
  bezeichnung: '',
  art: 'fahrmodul' as Bausteinart,
  /* AeA-07: drei Blöcke sind 45 Minuten — die häufigste Übungslänge. */
  regeldauerBloecke: '3',
  /* PRD-39: Ein neuer Baustein ist im Zweifel Inhalt, nicht Organisation. */
  imLeistungsversprechen: true,
  /* AeA-08: leer heißt „jederzeit" — die Regel, nicht die Ausnahme. */
  startfensterVon: '',
  startfensterBis: '',
  beschreibung: '',
  aktiv: true,
};

type Formular = typeof LEER;

function ausZeile(b: Bausteinzeile): Formular {
  return {
    schluessel: b.schluessel,
    bezeichnung: b.bezeichnung,
    art: b.art,
    regeldauerBloecke: String(b.regeldauerBloecke),
    imLeistungsversprechen: b.imLeistungsversprechen,
    startfensterVon: b.startfensterVon ?? '',
    startfensterBis: b.startfensterBis ?? '',
    beschreibung: b.beschreibung ?? '',
    aktiv: b.aktiv,
  };
}

export function Bausteine({
  darfKonfigurieren,
}: {
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Bausteinzeile[]>([]);
  /*
   * AeA-07: Wie lang ein Block ist. `null` heisst NICHT "unbekannt", sondern
   * "die Standorte fuehren verschiedene Raster" — dann steht hier die
   * Blockzahl ohne Minutenangabe, weil eine herausgegriffene Zahl fuer den
   * anderen Standort falsch waere.
   */
  const [blockMinuten, setzeBlockMinuten] = useState<number | null>(null);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  /** Welcher Baustein bearbeitet wird — `null` heisst: kein Formular offen. */
  const [bearbeitet, setzeBearbeitet] = useState<Bausteinzeile | null>(null);
  const [neu, setzeNeu] = useState(false);
  const [formular, setzeFormular] = useState<Formular>(LEER);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      const antwort = await ladeBausteine();
      setzeZeilen(antwort.bausteine);
      /*
       * NUR EINE ZAHL SCHALTET DIE MINUTEN FREI. Fehlt das Feld — eine
       * aeltere Fassung des Servers, ein Zwischenstand beim Ausrollen —,
       * stuende sonst "undefined min" in der Liste. Eine fehlende Angabe ist
       * dasselbe wie uneinige Standorte: keine Minuten, nur Bloecke.
       */
      setzeBlockMinuten(
        typeof antwort.blocklaengeMinuten === 'number' ? antwort.blocklaengeMinuten : null,
      );
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  function schliessen(): void {
    setzeBearbeitet(null);
    setzeNeu(false);
    setzeFormular(LEER);
  }

  async function absenden(e: FormEvent): Promise<void> {
    e.preventDefault();
    setzeMeldung(null);
    const gemeinsam = {
      bezeichnung: formular.bezeichnung.trim(),
      art: formular.art,
      regeldauerBloecke: Number(formular.regeldauerBloecke),
      imLeistungsversprechen: formular.imLeistungsversprechen,
      /* Beides oder keines — ein halbes Fenster lässt sich nicht verletzen. */
      startfensterVon: formular.startfensterVon === '' ? null : formular.startfensterVon,
      startfensterBis: formular.startfensterBis === '' ? null : formular.startfensterBis,
      beschreibung: formular.beschreibung.trim() === '' ? null : formular.beschreibung.trim(),
    };
    try {
      if (neu) {
        await bausteinAnlegen({ ...gemeinsam, schluessel: formular.schluessel.trim() });
        setzeMeldung('Der Baustein ist angelegt.');
      } else if (bearbeitet !== null) {
        await bausteinAendern(bearbeitet.schluessel, { ...gemeinsam, aktiv: formular.aktiv });
        setzeMeldung('Der Baustein ist geändert.');
      }
      schliessen();
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  /**
   * Wo der Baustein ueberall steckt — als Satz und nicht als vier Zahlen.
   * Wer stilllegt, entscheidet danach.
   */
  function verwendung(b: Bausteinzeile): string {
    const teile: string[] = [];
    if (b.verwendungProdukte > 0) teile.push(`${String(b.verwendungProdukte)} Produkte`);
    if (b.verwendungModule > 0) teile.push(`${String(b.verwendungModule)} Module`);
    if (b.verwendungPlaene > 0) teile.push(`${String(b.verwendungPlaene)} Planeinträge`);
    return teile.length === 0 ? 'nirgends verwendet' : teile.join(', ');
  }

  /* PRD-30: ab dem ersten Planeintrag ist die Art fest. */
  const artFest = bearbeitet !== null && bearbeitet.verwendungPlaene > 0;

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(zeilen.length, 'Baustein', 'Bausteine'));

  /*
   * SEITENTYP LISTE (ÄA-41): Zähler im Seitenkopf, „Baustein anlegen" in der
   * Leiste, der Katalog als Tabelle in einer Karte. Gepflegt wird weiter im
   * Fenster, weil der Ressourcenbedarf mit seiner eigenen Tabelle zum
   * Baustein gehört — in einer Seitenleiste von 360 px fände er keinen Platz.
   */
  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        {...(darfKonfigurieren
          ? {
              filter: (
                <button
                  type="button"
                  className={neu ? 'knopf' : 'knopf knopf--haupt'}
                  onClick={() => {
                    if (neu) {
                      schliessen();
                    } else {
                      setzeBearbeitet(null);
                      setzeNeu(true);
                      setzeFormular(LEER);
                    }
                  }}
                >
                  {neu ? 'Abbrechen' : 'Baustein anlegen'}
                </button>
              ),
            }
          : {})}
        hinweise={
          <>
            {/* PRD-29 */}
            <p className="hilfe">
              Die Bausteine, aus denen ein Training besteht. Dasselbe Wort steht im Programm
              eines Produkts, in der Eignung eines Moduls und im Tagesplan — deshalb ein Katalog
              und keine Zeilen je Produkt. Gelöscht wird nicht: Ein stillgelegter Baustein
              verschwindet aus jeder Auswahl, alles Bestehende bleibt lesbar.
            </p>
            {meldung !== null && (
              <p className="hinweiszeile" role="status">
                {meldung}
              </p>
            )}
          </>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">Bausteinkatalog</caption>
            <thead>
              <tr>
                <th scope="col">Schlüssel</th>
                <th scope="col">Bezeichnung</th>
                <th scope="col">Art</th>
                <th scope="col">Regeldauer</th>
                <th scope="col">Verwendung</th>
                <th scope="col">Status</th>
                {darfKonfigurieren && (
                  <th scope="col">
                    <span className="nur-vorlesen">Bearbeiten</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {zeilen.map((b) => (
                <tr key={b.schluessel} className={b.aktiv ? undefined : 'liste__zeile--blass'}>
                  <td>
                    <code>{b.schluessel}</code>
                  </td>
                  <td>
                    {b.bezeichnung}
                    {/*
                      PRD-39: Was NICHT im Leistungsversprechen steht, wird
                      ausgewiesen — nicht das Übliche. Die Liste soll auffallen
                      lassen, wo ein Baustein dem Kunden gegenüber verschwiegen
                      wird, nicht das Normale wiederholen.
                    */}
                    {!b.imLeistungsversprechen && (
                      <span className="hilfe"> · nicht im Leistungsversprechen</span>
                    )}
                    {/* AeA-08: Ein Zeitfenster ist die Ausnahme — es fällt auf. */}
                    {b.startfensterVon !== null && (
                      <span className="hilfe">
                        {' '}
                        · beginnt {b.startfensterVon}–{b.startfensterBis}
                      </span>
                    )}
                  </td>
                  <td>{BAUSTEINARTNAME[b.art]}</td>
                  <td>
                    {/*
                     * TAG-01 i.d.F. AeA-07: ein Block ist eine Viertelstunde.
                     * Die Minuten stehen dabei — eine Blockzahl allein sagt
                     * niemandem, ob das eine Dreiviertelstunde ist.
                     */}
                    {b.regeldauerBloecke === 1
                      ? '1 Block'
                      : `${String(b.regeldauerBloecke)} Blöcke`}
                    {blockMinuten !== null && (
                      <span className="hilfe">
                        {' '}
                        · {String(b.regeldauerBloecke * blockMinuten)} min
                      </span>
                    )}
                  </td>
                  <td>{verwendung(b)}</td>
                  <td>
                    <span className={`marke marke--${b.aktiv ? 'gut' : 'inaktiv'}`}>
                      {b.aktiv ? 'aktiv' : 'stillgelegt'}
                    </span>
                  </td>
                  {darfKonfigurieren && (
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        onClick={() => {
                          setzeNeu(false);
                          setzeBearbeitet(b);
                          setzeFormular(ausZeile(b));
                        }}
                      >
                        Bearbeiten
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {zeilen.length === 0 && !laedt && (
          <div className="liste__leer" role="status">
            Es ist kein Baustein angelegt.
          </div>
        )}
      </Liste>

      {/*
        ALLES ZU EINEM BAUSTEIN IN EINEM FENSTER.

        Bis hierher standen Formular UND Bedarfspflege unter der Tabelle. Bei
        vierzehn Bausteinen heißt das: Wer den letzten anfasst, sieht nach dem
        Klick nichts — die Maske hat reagiert, nur nicht sichtbar.

        Beides steht jetzt im Fenster, und zwar zusammen: Der Bedarf gehört
        zum Baustein, und wer ihn dort suchen muss, wo eben noch die Liste
        war, sucht ihn zweimal.
      */}
      {(neu || bearbeitet !== null) && (
        <Dialog
          titel={neu ? 'Neuer Baustein' : `Baustein ${bearbeitet?.schluessel ?? ''}`}
          unterzeile={neu ? undefined : bearbeitet?.bezeichnung}
          breite="breit"
          beiSchliessen={schliessen}
        >
        <form onSubmit={(e) => void absenden(e)}>
          {neu ? (
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="bsSchluessel">
                Schlüssel <span className="feld__pflicht">Pflicht</span>
              </label>
              <input
                id="bsSchluessel"
                className="feld__eingabe"
                value={formular.schluessel}
                onChange={(e) => {
                  setzeFormular({ ...formular, schluessel: e.target.value.toUpperCase() });
                }}
                placeholder="SCHRAEGLAGE"
              />
              <p className="feld__hilfe">
                Großbuchstaben, Ziffern und Unterstrich. Er wird vergeben, nicht aus der
                Bezeichnung errechnet — und er ist danach <strong>nicht mehr änderbar</strong>:
                Sechs Tabellen verweisen darauf.
              </p>
            </div>
          ) : (
            <div className="feld">
              <span className="feld__beschriftung">Schlüssel</span>
              <p>
                <code>{bearbeitet?.schluessel}</code>
              </p>
              <p className="feld__hilfe">
                Die Identität des Bausteins und nicht änderbar. Wird ein anderer Schlüssel
                gebraucht, legen Sie einen neuen Baustein an und legen diesen still.
              </p>
            </div>
          )}

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bsBezeichnung">
              Bezeichnung <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id="bsBezeichnung"
              className="feld__eingabe"
              value={formular.bezeichnung}
              onChange={(e) => {
                setzeFormular({ ...formular, bezeichnung: e.target.value });
              }}
              placeholder="Schräglage auf der Kreisbahn"
            />
            <p className="feld__hilfe">
              Dieses Wort steht in jeder Auswahl — im Produktprogramm, an den Modulen und im
              Tagesplan. Zwei aktive Bausteine dürfen nicht gleich heißen.
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bsArt">
              Art <span className="feld__pflicht">Pflicht</span>
            </label>
            <select
              id="bsArt"
              className="feld__eingabe"
              value={formular.art}
              disabled={artFest}
              onChange={(e) => {
                setzeFormular({ ...formular, art: e.target.value as Bausteinart });
              }}
            >
              {BAUSTEINARTEN.map((a) => (
                <option key={a} value={a}>
                  {BAUSTEINARTNAME[a]}
                </option>
              ))}
            </select>
            <p className="feld__hilfe">
              {artFest
                ? `Die Art ist fest, seit der Baustein in ${String(
                    bearbeitet?.verwendungPlaene ?? 0,
                  )} Planeinträgen steht. Sie entscheidet, ob ein Block in die Mindestdauer ohne `
                  + 'Pausen zählt — eine Änderung würde bereits gefahrene Tage '
                  + 'rückwirkend anders rechnen. Wird eine andere Art gebraucht, legen '
                  + 'Sie einen neuen Baustein an und legen diesen still.'
                : 'Sie ist keine Beschriftung, sondern eine Rechenregel: Eine Pause zählt nicht '
                  + 'in die Mindestdauer ohne Pausen, und die Pausenregeln greifen nur '
                  + 'an ihr. Ab dem ersten Planeintrag ist sie fest.'}
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bsDauer">
              Regeldauer in Blöcken
            </label>
            <input
              id="bsDauer"
              type="number"
              min="1"
              max="32"
              className="feld__eingabe feld__eingabe--schmal"
              value={formular.regeldauerBloecke}
              onChange={(e) => {
                setzeFormular({ ...formular, regeldauerBloecke: e.target.value });
              }}
            />
            <p className="feld__hilfe">
              Ein Block ist eine Viertelstunde: 45 Minuten sind{' '}
              <strong>drei</strong> Blöcke, 60 Minuten <strong>vier</strong>.
              {blockMinuten !== null &&
                formular.regeldauerBloecke !== '' &&
                Number(formular.regeldauerBloecke) > 0 &&
                ` Der eingetragene Wert ergibt ${String(
                  Number(formular.regeldauerBloecke) * blockMinuten,
                )} Minuten.`}{' '}
              Der Wert wirkt beim nächsten Planen — bereits geplante Tage behalten ihre Blöcke.
            </p>
          </div>

          {!OHNE_LEISTUNGSVERSPRECHEN.includes(formular.art) && (
          <div className="feld">
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={formular.imLeistungsversprechen}
                onChange={(e) => {
                  setzeFormular({ ...formular, imLeistungsversprechen: e.target.checked });
                }}
              />{' '}
              Im Leistungsversprechen
            </label>
            <p className="feld__hilfe">
              Dieser Baustein wird dem Kunden gegenüber benannt: Er erscheint in der
              Merkmalsliste jedes Produkts, das ihn enthält, und damit im Katalog.
              Ausschalten für Organisation — Begrüßung, Pause, Mittag und
              Abschluss finden statt, aber niemand kauft sie. Die{' '}
              <strong>Bezeichnung ist der Text</strong>, den der Kunde liest; einen zweiten
              Namen gibt es nicht.
            </p>
          </div>
          )}

          {/*
            AeA-08: DAS ZEITFENSTER IST DIE AUSNAHME, nicht die Regel.

            Es steht deshalb leer und wird nur dort gefüllt, wo es etwas
            bedeutet — heute beim Mittagessen. Ein Fenster an jeder Übung
            erzeugte an jeder einen Hinweis, und nach dem dritten liest ihn
            niemand mehr. Beide Felder oder keines: „ab 11:30" ohne Ende ließe
            sich nicht verletzen.

            Auch hier das Viertelstundenraster — die Blockgrenzen liegen
            dort, und ein Fenster ab 11:37 könnte kein Block treffen.
          */}
          <div className="feld">
            <span className="feld__beschriftung">Beginnt üblicherweise zwischen</span>
            <input
              type="time"
              step={900}
              className="feld__eingabe feld__eingabe--schmal"
              aria-label="Startfenster von"
              value={formular.startfensterVon}
              onChange={(e) => {
                setzeFormular({ ...formular, startfensterVon: e.target.value });
              }}
            />{' '}
            und{' '}
            <input
              type="time"
              step={900}
              className="feld__eingabe feld__eingabe--schmal"
              aria-label="Startfenster bis"
              value={formular.startfensterBis}
              onChange={(e) => {
                setzeFormular({ ...formular, startfensterBis: e.target.value });
              }}
            />
            {/* TAG-13 */}
            <p className="feld__hilfe">
              Leer heißt <strong>jederzeit</strong>. Eine Abweichung im Tagesplan ist ein
              Hinweis und kein Fehler: Ein Training, das um 13:00 anfängt, isst
              später zu Mittag — das ist richtig und soll planbar bleiben.
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bsBeschreibung">
              Beschreibung
            </label>
            <textarea
              id="bsBeschreibung"
              className="feld__eingabe"
              rows={3}
              value={formular.beschreibung}
              onChange={(e) => {
                setzeFormular({ ...formular, beschreibung: e.target.value });
              }}
            />
          </div>

          {!neu && (
            <div className="feld">
              <label className="liste__schalter">
                <input
                  type="checkbox"
                  checked={formular.aktiv}
                  onChange={(e) => {
                    setzeFormular({ ...formular, aktiv: e.target.checked });
                  }}
                />{' '}
                Aktiv
              </label>
              <p className="feld__hilfe">
                {bearbeitet !== null && verwendung(bearbeitet) !== 'nirgends verwendet'
                  ? `Stillgelegt verschwindet der Baustein aus jeder Auswahl. Er steckt heute in ${verwendung(
                      bearbeitet,
                    )} — das Bestehende bleibt lesbar, aber neu wählen lässt er sich nicht mehr.`
                  : 'Stillgelegt verschwindet der Baustein aus jeder Auswahl. Bestehendes bleibt '
                    + 'lesbar.'}
              </p>
            </div>
          )}

          <Abschlussreihe
            abbruch={
              <button type="button" className="knopf" onClick={schliessen}>
                Abbrechen
              </button>
            }
          >
            <button type="submit" className="knopf knopf--haupt">
              {neu ? 'Anlegen' : 'Speichern'}
            </button>
          </Abschlussreihe>
        </form>

          {/*
            DER BEDARF STEHT NEBEN DEM BAUSTEIN, NICHT DARIN.

            Er ist eine eigene Liste mit eigenem Speichern: Wer die Regeldauer
            ändert, will nicht zugleich über Trainerzahlen entscheiden. Und beim
            NEUEN Baustein gibt es ihn noch nicht — ein Bedarf braucht einen
            Schlüssel, an dem er hängt.
          */}
          {bearbeitet !== null && (
            <Bedarfspflege
              baustein={bearbeitet.schluessel}
              bezeichnung={bearbeitet.bezeichnung}
              darfKonfigurieren={darfKonfigurieren}
            />
          )}
        </Dialog>
      )}
    </>
  );
}

/**
 * Der Ressourcenbedarf eines Bausteins (PRD-29, TAG-23).
 *
 * ============================================================================
 * EIN MAL GEPFLEGT, AN JEDEM TAG GÜLTIG
 * ============================================================================
 *
 * PRD-29: „Jeder Baustein trägt Regeldauer in Blöcken und seinen
 * Ressourcenbedarf." Dass „Bremsen und Ausweichen" ein Fahrmodul und einen
 * Trainer braucht, ändert sich nicht von Dienstag auf Mittwoch — es hier zu
 * hinterlegen erspart es an jedem einzelnen Tag.
 *
 * Der Auftraggeber hat den Grund am 07.09.2026 benannt: „Bei der
 * Tagesplanung werden Ressourcen und keine Bausteine geplant. Die Ressourcen
 * müssen auf Basis der Bausteine geplant werden."
 *
 * ============================================================================
 * ART ODER EIGENSCHAFT — EINE LISTE, WEIL ES EINE ENTSCHEIDUNG IST
 * ============================================================================
 *
 * Die Datenhaltung kennt zwei Wege zu einer Ressource: ihre ART („ein Raum")
 * oder eine EIGENSCHAFT („das Bistro"); der dritte über eine Gruppe ist mit
 * ÄA-15 entfallen. RES-05 lässt GENAU EINEN zu, und `bedarf_genau_eine_suchart`
 * setzt es durch — deshalb steht hier ein Auswahlfeld und nicht zwei.
 *
 * Bis zum 10.09.2026 gab es nur die Art, weil keine Eigenschaft gepflegt war.
 * Der Auftraggeber hat den Fall genannt, der das ändert: „bei den bausteinen
 * müsste für die mittagspausen als ressource das bistro auswählbar sein."
 * „Ein Raum" trifft dafür nicht — es träfe auch die sechs Schulungsräume.
 *
 * WELCHE Eigenschaften es gibt, weiß nur der Bestand. Sie kommen deshalb mit
 * der Antwort des Servers, samt der Zahl der Ressourcen, die sie tragen: Eine
 * Eigenschaft ohne Träger deckt keinen Bedarf, und das gehört vor den Klick.
 */
function Bedarfspflege({
  baustein,
  bezeichnung,
  darfKonfigurieren,
}: {
  readonly baustein: string;
  readonly bezeichnung: string;
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Bedarfseingabe[]>([]);
  /*
    WAS DIE ART UEBLICHERWEISE BRAUCHT (PRD-29).

    Er kommt von der API und wird nirgends gespeichert. Angeboten wird er nur,
    solange die Liste LEER ist: Wer schon gepflegt hat, hat entschieden, und
    ein Knopf, der eine Zeile danebensetzt, macht aus einer Entscheidung ein
    Versehen.
  */
  const [vorschlag, setzeVorschlag] = useState<readonly Bedarfseingabe[]>([]);
  /** Was der Bestand an Eigenschaften kennt (RES-02) — vom Server, nicht erfunden. */
  const [eigenschaften, setzeEigenschaften] = useState<readonly Bedarfseigenschaft[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  useEffect(() => {
    let verworfen = false;
    setzeLaedt(true);
    setzeMeldung(null);
    ladeBedarf(baustein)
      .then((a) => {
        if (verworfen) return;
        setzeZeilen(
          a.bedarf.map((b) => ({
            reihenfolge: b.reihenfolge,
            bezeichnung: b.bezeichnung,
            ressourcenart: b.ressourcenart,
            eigenschaftart: b.eigenschaftart,
            eigenschaftschluessel: b.eigenschaftschluessel,
            anzahl: b.anzahl,
            zwingend: b.zwingend,
            buchungsrelevant: b.buchungsrelevant,
            vorbereitungMinuten: b.vorbereitungMinuten,
            nachbereitungMinuten: b.nachbereitungMinuten,
          })),
        );
        setzeVorschlag(a.vorschlag);
        setzeEigenschaften(a.eigenschaften);
        setzeLaedt(false);
      })
      .catch((e: unknown) => {
        if (verworfen) return;
        setzeFehler(e instanceof ApiFehler ? e : null);
        setzeLaedt(false);
      });
    return () => {
      verworfen = true;
    };
  }, [baustein]);

  /** Die Reihenfolge ist die POSITION — sie steht nicht als Feld daneben. */
  const verdichte = (liste: readonly Bedarfseingabe[]): readonly Bedarfseingabe[] =>
    liste.map((z, i) => ({ ...z, reihenfolge: i + 1 }));

  function aendere(stelle: number, teil: Partial<Bedarfseingabe>): void {
    setzeZeilen((alt) => verdichte(alt.map((z, i) => (i === stelle ? { ...z, ...teil } : z))));
  }

  async function speichern(): Promise<void> {
    setzeMeldung(null);
    try {
      const { gesetzt } = await bedarfSetzen(baustein, verdichte(zeilen));
      setzeFehler(null);
      setzeMeldung(`${String(gesetzt)} Bedarfe gespeichert.`);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Ressourcenbedarf von {bezeichnung}</h2>
        <small>Was diese Übung braucht — an jedem Tag</small>
      </div>

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {laedt ? (
        <p className="hinweiszeile">Wird geladen …</p>
      ) : zeilen.length === 0 ? (
        <>
          <p className="hilfe">
            Für diesen Baustein ist kein Bedarf hinterlegt. Die Tagesplanung kann dann nicht
            prüfen, ob eine Übung vollständig besetzt ist — „kein Trainer zugeteilt" ist von
            „braucht keinen Trainer" nicht zu unterscheiden.
          </p>
          {/*
            DER VORSCHLAG STEHT NUR HIER — bei leerer Liste.

            Er sagt, wie der Regelfall dieser Bausteinart aussieht, und er ist
            ein VORSCHLAG: Übernehmen setzt die Zeile nur ins Formular, gespeichert
            wird sie mit „Bedarf speichern" wie jede getippte Zeile. Bis dahin
            lässt sie sich ändern.
          */}
          {darfKonfigurieren && vorschlag.length > 0 && (
            <p className="hinweiszeile">
              Üblich für diese Art:{' '}
              <strong>
                {vorschlag
                  .map(
                    (v) =>
                      `${String(v.anzahl)}× ${v.bezeichnung}${v.zwingend ? ', zwingend' : ''}`,
                  )
                  .join(' · ')}
              </strong>{' '}
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => {
                  setzeMeldung(null);
                  setzeZeilen(verdichte(vorschlag));
                }}
              >
                Standardbedarf übernehmen
              </button>
            </p>
          )}
        </>
      ) : (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Ressourcenbedarf">
            <thead>
              <tr>
                <th scope="col">Bezeichnung</th>
                <th scope="col">Art</th>
                <th scope="col">Anzahl</th>
                <th scope="col">Zwingend</th>
                <th scope="col">Rüstzeit</th>
                {darfKonfigurieren && <th scope="col">Handlung</th>}
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z, i) => (
                <tr key={`${String(i)}-${z.bezeichnung}`}>
                  <td>
                    <input
                      className="feld__eingabe"
                      aria-label={`Bezeichnung ${String(i + 1)}`}
                      value={z.bezeichnung}
                      disabled={!darfKonfigurieren}
                      onChange={(e) => {
                        aendere(i, { bezeichnung: e.target.value });
                      }}
                    />
                  </td>
                  <td>
                    {/*
                      EINE LISTE FÜR BEIDE WEGE (RES-05).
  
                      Oben die Arten, unten die Eigenschaften des Bestands. Wer
                      „Bistro" wählt, stellt den Bedarf über die Eigenschaft und
                      trifft damit genau das Bistro — „ein Raum" träfe auch die
                      sechs Schulungsräume. Genau eines von beiden gilt, deshalb
                      setzt die Wahl das andere auf `null`.
                    */}
                    <select
                      aria-label={`Art ${String(i + 1)}`}
                      value={
                        z.eigenschaftschluessel === null || z.eigenschaftschluessel === undefined
                          ? `art:${z.ressourcenart ?? ''}`
                          : `eig:${z.eigenschaftart ?? ''}:${z.eigenschaftschluessel}`
                      }
                      disabled={!darfKonfigurieren}
                      onChange={(e) => {
                        const [weg, a, b] = e.target.value.split(':');
                        aendere(
                          i,
                          weg === 'eig'
                            ? {
                                ressourcenart: null,
                                eigenschaftart: a ?? null,
                                eigenschaftschluessel: b ?? null,
                              }
                            : {
                                ressourcenart: a ?? null,
                                eigenschaftart: null,
                                eigenschaftschluessel: null,
                              },
                        );
                      }}
                    >
                      <optgroup label="Nach Art">
                        {RESSOURCENARTEN.map((a) => (
                          <option key={a} value={`art:${a}`}>
                            {RESSOURCENARTNAME[a]}
                          </option>
                        ))}
                      </optgroup>
                      {eigenschaften.length > 0 && (
                        <optgroup label="Nach Eigenschaft">
                          {eigenschaften.map((e) => (
                            <option
                              key={`${e.art}:${e.schluessel}`}
                              value={`eig:${e.art}:${e.schluessel}`}
                            >
                              {e.bezeichnung} ({e.ressourcen})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      min={1}
                      className="feld__eingabe feld__eingabe--schmal"
                      aria-label={`Anzahl ${String(i + 1)}`}
                      value={z.anzahl}
                      disabled={!darfKonfigurieren}
                      onChange={(e) => {
                        aendere(i, { anzahl: Number(e.target.value) });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Zwingend ${String(i + 1)}`}
                      checked={z.zwingend}
                      disabled={!darfKonfigurieren}
                      onChange={(e) => {
                        aendere(i, { zwingend: e.target.checked });
                      }}
                    />
                  </td>
                  <td>
                    {/*
                      TAG-23: Rüst- und Wechselzeiten. Sie stehen am Bedarf und
                      nicht am Tagesplan — ein Modul, das zwanzig Minuten Umbau
                      braucht, braucht sie an jedem Tag.
                    */}
                    <input
                      type="number"
                      min={0}
                      step={5}
                      className="feld__eingabe feld__eingabe--schmal"
                      aria-label={`Rüstzeit ${String(i + 1)}`}
                      value={z.vorbereitungMinuten}
                      disabled={!darfKonfigurieren}
                      onChange={(e) => {
                        aendere(i, { vorbereitungMinuten: Number(e.target.value) });
                      }}
                    />{' '}
                    <span className="hilfe">min</span>
                  </td>
                  {darfKonfigurieren && (
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        onClick={() => {
                          setzeZeilen((alt) => verdichte(alt.filter((_, k) => k !== i)));
                        }}
                      >
                        Entfernen
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {darfKonfigurieren && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf"
            onClick={() => {
              setzeZeilen((alt) =>
                verdichte([
                  ...alt,
                  {
                    reihenfolge: alt.length + 1,
                    bezeichnung: '',
                    ressourcenart: 'modul',
                    anzahl: 1,
                    /* Ein neuer Bedarf ist im Zweifel zwingend — sonst prüft er nichts. */
                    zwingend: true,
                    buchungsrelevant: false,
                    vorbereitungMinuten: 0,
                    nachbereitungMinuten: 0,
                  },
                ]),
              );
            }}
          >
            Bedarf hinzufügen
          </button>
          <button type="button" className="knopf knopf--haupt" onClick={() => void speichern()}>
            Bedarf speichern
          </button>
        </div>
      )}
    </div>
  );
}
